import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#1b325f", primaryBlue: "#282f81", accentCyan: "#11abea",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0"
};

const PurchaseIndent = ({ screenAction, triggerSave }) => {
  const today = new Date().toISOString().split('T')[0];

  const initHeader = {
    indent_number: "", indent_date: today, location_name: "", supplier_name: "",
    estimated_cost: 0, indent_status: "Pending"
  };

  const initLine = {
    medicine_id: "", medicine_name: "", unit: "Strip", 
    purchase_rate: "", mrp: "", gst_percent: "", // 🔥 Added mrp and gst
    indent_qty: "", estimated_value: 0
  };

  const [header, setHeader] = useState(initHeader);
  const [lines, setLines] = useState([initLine]);
  
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const [locationsList, setLocationsList] = useState([]);
  const [suppliersList, setSuppliersList] = useState([]);
  const [medicinesList, setMedicinesList] = useState([]);

  // --- STYLES ---
  const compactRowStyle = { display: 'flex', alignItems: 'center', marginBottom: '8px' };
  const compactLabelStyle = { width: '120px', fontSize: '13px', color: theme.textMain, fontWeight: '600' };
  const compactInputStyle = { 
    flex: 1, padding: '6px 8px', fontSize: '13px', border: 'none', 
    borderBottom: `1px solid ${theme.borderLight}`, outline: 'none', 
    backgroundColor: isDisabled ? 'transparent' : '#fcfcfc' 
  };
  const cellStyle = { border: `1px solid ${theme.borderLight}`, padding: '0', margin: '0' };
  const cellInputStyle = { width: '100%', boxSizing: 'border-box', border: 'none', padding: '8px 6px', fontSize: '12px', backgroundColor: 'transparent', outline: 'none' };
  const readOnlyCell = { ...cellInputStyle, backgroundColor: '#f1f5f9', fontWeight: '600', color: theme.textMain };

  // --- DATA FETCHING ---
  const fetchBaseData = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const [locRes, supRes, medRes] = await Promise.all([
        fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/locations/all?employee_id=${user.id}`),
        fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/suppliers?employee_id=${user.id}`),
        fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/medicines/all?employee_id=${user.id}`)
      ]);
      setLocationsList(await locRes.json() || []);
      setSuppliersList(await supRes.json() || []);
      setMedicinesList(await medRes.json() || []);
    } catch (e) { console.error("Error loading base data", e); }
  };

  const fetchNextId = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/medicine-indent/id?employee_id=${user.id}`);
    const data = await res.json();
    setHeader(prev => ({ ...prev, indent_number: data.indent_number }));
  };

  useEffect(() => { fetchBaseData(); fetchNextId(); }, []);

  // --- CALCULATIONS ---
  useEffect(() => {
    let total = 0;
    lines.forEach(l => { total += parseFloat(l.estimated_value) || 0; });
    setHeader(prev => ({ ...prev, estimated_cost: total.toFixed(2) }));
  }, [lines]);

  const handleHeaderChange = (e) => setHeader({ ...header, [e.target.name]: e.target.value });

  const handleLineChange = (index, field, value) => {
    const updated = [...lines];
    const row = updated[index];
    row[field] = value;

    // 🔥 Auto-fill MRP and GST% from Medicine Master
    if (field === "medicine_id") {
      const med = medicinesList.find(m => m.medicine_id === value);
      if (med) {
        row.medicine_name = med.medicine_name;
        row.purchase_rate = med.purchase_price;
        row.mrp = med.mrp;
        row.gst_percent = med.gst_percentage;
      }
    }

    const qty = parseFloat(row.indent_qty) || 0;
    const rate = parseFloat(row.purchase_rate) || 0;
    row.estimated_value = (qty * rate).toFixed(2);

    updated[index] = row;
    setLines(updated);
  };

  const addRow = () => setLines([...lines, { ...initLine }]);
  const removeRow = (index) => setLines(lines.filter((_, i) => i !== index));

  // --- SEARCH & MODES ---
  const searchIndent = async (text) => {
    setSearchText(text);
    if (text.length < 3) { setSearchResults([]); setShowDropdown(false); return; }
    const user = JSON.parse(localStorage.getItem("user"));
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/medicine-indent/search?q=${text}&employee_id=${user.id}`);
    setSearchResults(await res.json());
    setShowDropdown(true);
  };

  const selectIndent = async (indent) => {
    setSearchText(`${indent.indent_number} | ${indent.location_name}`);
    setShowDropdown(false);
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/medicine-indent/${indent.indent_number}`);
    const data = await res.json();
    
    if(data.header) {
      setHeader({ 
        ...data.header, 
        indent_date: data.header.indent_date ? data.header.indent_date.split('T')[0] : "" 
      });
      setLines(data.details.length > 0 ? data.details : [initLine]);
    }
    
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  const clearForm = async () => {
    setHeader(initHeader); setLines([initLine]); setSearchText("");
    await fetchNextId();
  };

  useEffect(() => {
    if (screenAction === "add" || screenAction === "refresh") { clearForm(); setIsDisabled(false); }
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "print") printForm();
  }, [screenAction]);

  useEffect(() => { if (triggerSave > 0 && screenAction === "save") handleSubmit(); }, [triggerSave]);

  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!header.location_name) return alert("Requesting Location is required.");
    
    const validLines = lines.filter(l => l.medicine_id !== "");
    if (validLines.length === 0) return alert("Add at least one medicine item.");

    const payload = { header: { ...header, created_by: user.id }, details: validLines, isEdit: !!header.created_at };
    
    try {
      const res = await fetch("https://hmsapi.emedhatechnologies.com/master/api/api/medicine-indent", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert("Purchase Indent Saved Successfully!");
        if (!payload.isEdit) clearForm();
      } else alert("Error saving Indent");
    } catch (err) { alert("Network Error"); }
  };

  // --- PRINT FORM ---
  const printForm = () => {
    if (!header.indent_number) return;
    const newWindow = window.open("", "", "width=900,height=700");
    const htmlContent = `
      <html><head><title>Indent - ${header.indent_number}</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 30px; font-size: 13px; color: #333; }
        .title-box { display: flex; justify-content: space-between; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
        .title-box h1 { margin: 0; color: #282f81; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; background: #f8fafc; padding: 15px; border-left: 4px solid #11abea; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f4f4; color: #282f81; }
        .text-right { text-align: right; }
        .totals-box { width: 250px; float: right; border: 1px solid #ddd; padding: 10px; border-radius: 4px; background: #f0fdf4; }
      </style></head>
      <body>
        <div class="title-box">
          <div><h1>EMEDHA CARE HOSPITAL</h1><p>Internal Purchase Indent (Request Note)</p></div>
          <div style="text-align:right"><h2>${header.indent_number}</h2><p>Date: ${header.indent_date}</p></div>
        </div>
        <div class="info-grid">
          <div><strong>Requesting Location:</strong><br/>${header.location_name}</div>
          <div><strong>Preferred Supplier:</strong><br/>${header.supplier_name || 'Any Appproved Supplier'}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>SI</th><th>Medicine Name</th><th>Unit</th><th class="text-right">Pur Rate</th>
              <th class="text-right">MRP</th><th class="text-right">GST%</th>
              <th class="text-right">Req Qty</th><th class="text-right">Est Value</th>
            </tr>
          </thead>
          <tbody>
            ${lines.map((l, i) => `
              <tr>
                <td>${i + 1}</td><td>${l.medicine_id} - ${l.medicine_name}</td><td>${l.unit}</td>
                <td class="text-right">₹${l.purchase_rate}</td><td class="text-right">₹${l.mrp}</td><td class="text-right">${l.gst_percent}%</td>
                <td class="text-right" style="font-weight:bold;">${l.indent_qty}</td><td class="text-right">₹${l.estimated_value}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="totals-box">
          <div style="display: flex; justify-content: space-between; font-weight: bold; color: #166534;">
            <span>Total Est. Cost:</span><span>₹${header.estimated_cost}</span>
          </div>
        </div>
      </body></html>
    `;
    newWindow.document.write(htmlContent); newWindow.document.close();
    setTimeout(() => { newWindow.print(); newWindow.close(); }, 500);
  };

  return (
    <div className="registration-container fade-in" style={{ backgroundColor: theme.bgLight, padding: '10px 20px', minHeight: '100vh' }}>
      
      {/* SEARCH BAR */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', marginBottom: '20px', gap: '15px' }}>
          <div className="patient-search" style={{ position: 'relative', width: '350px', margin: 0 }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 'bold', color: theme.primaryDark }}>Search Indent</label>
            <input type="text" value={searchText} placeholder="Search Indent No, Location..." onChange={(e) => searchIndent(e.target.value)}
              style={{ width: '100%', padding: '8px 16px', borderRadius: '4px', border: `1px solid ${theme.borderLight}`, outline: 'none' }} />
            {showDropdown && searchResults.length > 0 && (
              <div style={{ position: 'absolute', top: '105%', left: 0, width: '100%', background: theme.cardBg, border: `1px solid ${theme.borderLight}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '200px', overflowY: 'auto' }}>
                {searchResults.map(p => (
                  <div key={p.indent_number} onClick={() => selectIndent(p)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${theme.borderLight}`, fontSize: '12px' }}>
                    <strong style={{ color: theme.primaryDark }}>{p.indent_number}</strong> &nbsp; | &nbsp; {p.location_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ backgroundColor: '#fff', border: `1px solid ${theme.borderLight}`, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', padding: '20px' }}>
        
        {/* HEADER SECTION */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '40px', marginBottom: '20px', paddingBottom: '15px', borderBottom: `2px solid ${theme.primaryDark}` }}>
          
          <div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Indent Number:</div>
              <input type="text" name="indent_number" value={header.indent_number} disabled style={{...compactInputStyle, fontWeight: 'bold', color: theme.primaryBlue}} />
            </div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Indent Date:</div>
              <input type="date" name="indent_date" value={header.indent_date} onChange={handleHeaderChange} disabled={isDisabled} style={compactInputStyle} />
            </div>
          </div>

          <div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Requesting Loc:</div>
              <select name="location_name" value={header.location_name} onChange={handleHeaderChange} disabled={isDisabled} style={compactInputStyle}>
                <option value="">Select Location...</option>
                {locationsList.map((loc, i) => <option key={i} value={loc.location_name}>{loc.location_name}</option>)}
              </select>
            </div>
            <div style={compactRowStyle}>
              <div style={compactLabelStyle}>Pref. Supplier:</div>
              <select name="supplier_name" value={header.supplier_name} onChange={handleHeaderChange} disabled={isDisabled} style={compactInputStyle}>
                <option value="">Any Supplier</option>
                {suppliersList.map((sup, i) => <option key={i} value={sup.Supp_Nm}>{sup.Supp_Nm}</option>)}
              </select>
            </div>
          </div>

          <div style={{ padding: '10px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px' }}>
            <div style={{...compactRowStyle, justifyContent: 'space-between'}}>
              <span style={{ fontSize: '13px', color: '#166534', fontWeight: 'bold' }}>Est. Total Cost:</span>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: theme.primaryBlue }}>₹{header.estimated_cost}</span>
            </div>
            <div style={{...compactRowStyle, justifyContent: 'space-between', marginTop: '10px'}}>
              <span style={{ fontSize: '13px', color: '#333' }}>Indent Status:</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: header.indent_status === 'Pending' ? '#e84118' : '#16a085' }}>{header.indent_status}</span>
            </div>
          </div>

        </div>

        {/* LINE ITEMS DATA GRID */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', color: theme.primaryDark }}>Requested Items</h3>
          {!isDisabled && <button type="button" onClick={addRow} style={{ background: theme.primaryBlue, color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>+ Add Row</button>}
        </div>
        
        <div style={{ overflowX: 'auto', border: `1px solid ${theme.borderLight}`, borderRadius: '4px' }}>
          <table style={{ width: '100%', minWidth: '1000px', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead style={{ backgroundColor: theme.primaryDark, color: '#fff' }}> 
              <tr>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '40px', textAlign: 'center' }}>SI</th>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '150px' }}>Medicine ID</th>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '220px' }}>Medicine Name</th>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '90px', textAlign: 'center' }}>Unit</th>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '90px', textAlign: 'right' }}>Pur Rate (₹)</th>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '80px', textAlign: 'right' }}>MRP (₹)</th>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '60px', textAlign: 'right' }}>GST %</th>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '110px', textAlign: 'right', color: theme.accentCyan }}>Indent Qty</th>
                <th style={{ ...cellStyle, padding: '10px 6px', width: '110px', textAlign: 'right' }}>Est Value (₹)</th>
                {!isDisabled && <th style={{ ...cellStyle, width: '40px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                  <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f8fafc' }}>{idx + 1}</td>
                  
                  <td style={cellStyle}>
                    <select value={line.medicine_id} onChange={(e) => handleLineChange(idx, 'medicine_id', e.target.value)} disabled={isDisabled} style={cellInputStyle}>
                      <option value=""></option>
                      {medicinesList.map(m => <option key={m.medicine_id} value={m.medicine_id}>{m.medicine_id}</option>)}
                    </select>
                  </td>
                  
                  <td style={cellStyle}><input type="text" value={line.medicine_name} disabled style={readOnlyCell} title={line.medicine_name} /></td>
                  
                  <td style={cellStyle}>
                    <select value={line.unit} onChange={(e) => handleLineChange(idx, 'unit', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'center'}}>
                      <option>Box</option><option>Strip</option><option>Loose</option>
                    </select>
                  </td>
                  
                  <td style={cellStyle}><input type="number" value={line.purchase_rate} disabled style={{...readOnlyCell, textAlign: 'right'}} /></td>
                  <td style={cellStyle}><input type="number" value={line.mrp} disabled style={{...readOnlyCell, textAlign: 'right'}} /></td>
                  <td style={cellStyle}><input type="number" value={line.gst_percent} disabled style={{...readOnlyCell, textAlign: 'right'}} /></td>
                  
                  <td style={cellStyle}><input type="number" value={line.indent_qty} onChange={(e) => handleLineChange(idx, 'indent_qty', e.target.value)} disabled={isDisabled} style={{...cellInputStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '13px', backgroundColor: '#fff'}} placeholder="0" /></td>
                  
                  <td style={cellStyle}><input type="text" value={line.estimated_value} disabled style={{...readOnlyCell, textAlign: 'right'}} /></td>
                  
                  {!isDisabled && <td style={{ ...cellStyle, textAlign: 'center', backgroundColor: '#fff' }}><button type="button" onClick={() => removeRow(idx)} style={{ color: 'red', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}>X</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default PurchaseIndent;